import { useState } from "react";
import { Lock, Sparkles, Check, Info, ArrowRight, Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import { createCheckout } from "@/lib/api";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

// Feature comparison rows for the Summary vs Closing Strategy table.
// Each row: { label, summary, closing, why } — `why` powers the small
// (i) "why this matters" popover beneath the row.
const FEATURE_ROWS = [
    {
        label: "Organized issues with severity",
        summary: true, closing: true,
        why: "We break the inspection report into discrete issues with severity tags so you don't have to re-read 80 pages of inspector prose. Same content, scannable in minutes.",
    },
    {
        label: "Categorized findings (foundation, electrical, HVAC, roof…)",
        summary: true, closing: true,
        why: "Same problem clustered by system, so you can talk to one contractor at a time and avoid double-counting overlap between the report and the quotes you'll collect.",
    },
    {
        label: "Plain-English explanations",
        summary: true, closing: true,
        why: "Each finding rewritten in clear language — what it is, what it usually costs, and what's worth worrying about. No jargon, no fluff.",
    },
    {
        label: "ZIP-localized repair exposure ranges",
        summary: true, closing: true,
        why: "Broad planning ranges drawn from real contractor quotes in your ZIP. For budgeting and negotiation framing — not for use as actual bids.",
    },
    {
        label: "Prioritized negotiation leverage list",
        summary: false, closing: true,
        why: "Ranks which 3-4 findings to lead with at the negotiation table and which to set aside. Avoids the rookie mistake of asking for everything at once.",
    },
    {
        label: "Recommended buyer-side questions",
        summary: false, closing: true,
        why: "Exact wording for what to ask the seller, the listing agent, and your own contractor — sequenced so each answer informs the next ask.",
    },
    {
        label: "Seller-credit math with ask-language",
        summary: false, closing: true,
        why: "Calculates the dollar credit you can reasonably request, framed in language sellers and listing agents accept rather than reject.",
    },
    {
        label: "Downloadable Closing Strategy memo",
        summary: false, closing: true,
        why: "A one-page brief you can forward to your agent or attorney before the response deadline. Removes the 'now what?' moment after you read your report.",
    },
];

// Compact investor plan snapshot — full feature list lives on /pricing.
const INVESTOR_SNAPSHOTS = [
    {
        id: "investor_lite", name: "Investor Lite", price: "$29",
        tagline: "Casual investors reviewing occasional deals.",
        quota: "3 reports / month",
        bullets: ["ZIP-localized repair exposure", "Basic issue prioritization", "PDF + share links"],
    },
    {
        id: "investor_pro", name: "Investor Pro", price: "$79",
        tagline: "Active investors comparing multiple properties.",
        quota: "10 reports / month",
        highlight: true,
        bullets: ["Closing Strategy on every report", "Deal comparison dashboard", "Negotiation leverage scoring"],
    },
    {
        id: "investor_plus", name: "Investor Plus", price: "$149",
        tagline: "High-volume investors, wholesalers, flippers.",
        quota: "25 reports / month",
        bullets: ["Portfolio / pipeline dashboard", "Quote tracking + calibration", "Bulk upload queue"],
    },
];

function FeatureCell({ included }) {
    return included ? (
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-bl-teal-tint">
            <Check className="w-3 h-3 text-bl-primary" strokeWidth={3} />
        </span>
    ) : (
        <span className="inline-block w-5 h-px bg-bl-muted/40" aria-label="not included" />
    );
}

export default function Paywall({ uploadId, ownedTier, onSuccess: _onSuccess }) {
    const [loading, setLoading] = useState(null);
    const [openRow, setOpenRow] = useState(null);

    const isUpgrade = ownedTier === "summary";

    const start = async (productId) => {
        try {
            setLoading(productId);
            track("checkout_initiated", { product_id: productId, upload_id: uploadId });
            const r = await createCheckout(productId, uploadId);
            window.location.href = r.url;
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not start checkout. Please try again.",
            );
            setLoading(null);
        }
    };

    return (
        <div className="space-y-6" data-testid="paywall">
            {/* Header card */}
            <div className="bl-card p-6 sm:p-8 md:p-10">
                <div className="bl-eyebrow w-fit">
                    <Lock className="w-3.5 h-3.5" /> Unlock full report
                </div>
                <h2 className="mt-4 text-[1.5rem] sm:text-2xl md:text-4xl font-display font-semibold tracking-tight text-bl leading-tight">
                    One report. Potentially{" "}
                    <span className="font-italic-accent text-bl-primary">
                        thousands in leverage.
                    </span>
                </h2>
                <p className="mt-3 text-bl max-w-2xl leading-relaxed">
                    Buy once. Saved to your dashboard. Designed to help you negotiate with
                    clarity — not noise.
                </p>

                {/* Feature comparison table */}
                <div className="mt-8 rounded-xl border border-bl bg-bl-surface overflow-hidden" data-testid="paywall-comparison-table">
                    {/* Column headers */}
                    <div className="grid grid-cols-[1fr_auto_auto] gap-3 sm:gap-6 px-4 sm:px-6 py-4 bg-bl-cream border-b border-bl">
                        <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                            What you get
                        </div>
                        {!isUpgrade && (
                            <div className="text-center min-w-[100px]">
                                <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                                    Inspection Summary
                                </div>
                                <div className="mt-1 font-display font-semibold text-bl">
                                    $49
                                </div>
                            </div>
                        )}
                        <div className="text-center min-w-[100px]">
                            <div className="text-[10px] tracking-[0.18em] uppercase text-bl-primary">
                                Closing Strategy
                            </div>
                            <div className="mt-1 font-display font-semibold text-bl">
                                {isUpgrade ? "$50" : "$99"}
                            </div>
                            <div className="mt-0.5">
                                <span className="bl-badge bl-badge-neutral text-[9px]">
                                    <Sparkles className="w-2.5 h-2.5" /> Most popular
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Feature rows */}
                    <div className="divide-y divide-bl">
                        {FEATURE_ROWS.map((row, idx) => {
                            const open = openRow === idx;
                            return (
                                <div key={row.label} className="px-4 sm:px-6 py-3 sm:py-4" data-testid={`paywall-row-${idx}`}>
                                    <div className="grid grid-cols-[1fr_auto_auto] gap-3 sm:gap-6 items-center">
                                        <div className="flex items-start gap-2 min-w-0">
                                            <button
                                                type="button"
                                                onClick={() => setOpenRow(open ? null : idx)}
                                                className="mt-0.5 shrink-0 text-bl-muted hover:text-bl-primary transition-colors"
                                                aria-label={`Why ${row.label} matters`}
                                                data-testid={`paywall-why-${idx}`}
                                            >
                                                <Info className="w-3.5 h-3.5" />
                                            </button>
                                            <span className="text-sm text-bl leading-snug">
                                                {row.label}
                                            </span>
                                        </div>
                                        {!isUpgrade && (
                                            <div className="text-center min-w-[100px]">
                                                <FeatureCell included={row.summary} />
                                            </div>
                                        )}
                                        <div className="text-center min-w-[100px]">
                                            <FeatureCell included={row.closing} />
                                        </div>
                                    </div>
                                    {open && (
                                        <div
                                            className="mt-3 ml-6 rounded-lg bg-bl-cream border-l-2 border-bl-primary px-4 py-3 text-xs text-bl-muted leading-relaxed"
                                            data-testid={`paywall-why-body-${idx}`}
                                        >
                                            <span className="text-[10px] tracking-[0.16em] uppercase text-bl-primary">
                                                Why this matters
                                            </span>
                                            <p className="mt-1.5 text-bl">{row.why}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* CTA row */}
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                    {!isUpgrade && (
                        <button
                            disabled={loading === "summary"}
                            onClick={() => start("summary")}
                            className="bl-btn-secondary w-full"
                            data-testid="paywall-buy-summary-btn"
                        >
                            {loading === "summary" ? "Redirecting…" : "Buy Summary — $49"}
                        </button>
                    )}
                    <button
                        disabled={loading === (isUpgrade ? "upgrade" : "closing_strategy")}
                        onClick={() => start(isUpgrade ? "upgrade" : "closing_strategy")}
                        className={`bl-btn-primary w-full ${isUpgrade ? "" : ""}`}
                        data-testid="paywall-buy-closing-btn"
                    >
                        {loading
                            ? "Redirecting…"
                            : isUpgrade
                              ? "Upgrade to Closing Strategy — $50"
                              : "Buy Closing Strategy — $99"}
                    </button>
                </div>

                <p className="mt-5 text-xs text-bl-muted">
                    Secure checkout via Stripe. Reports save to your dashboard automatically
                    after payment. You will never be asked to repurchase a report you
                    already own.
                </p>
            </div>

            {/* Investor section */}
            <div
                className="bl-card p-6 sm:p-8 md:p-10 bl-grad-teal-tint-soft border-bl-primary/30"
                data-testid="paywall-investor-block"
            >
                <div className="flex items-start gap-4 flex-wrap">
                    <div className="w-10 h-10 rounded-lg bg-bl-teal-tint flex items-center justify-center text-bl-primary shrink-0">
                        <Briefcase className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="bl-eyebrow w-fit">For investors</div>
                        <h3 className="mt-2 font-display font-semibold text-bl text-xl sm:text-2xl leading-tight">
                            Reviewing more than one property?
                        </h3>
                        <p className="mt-2 text-sm text-bl-muted leading-relaxed max-w-2xl">
                            If inspection reports are part of your weekly workflow, a
                            monthly subscription is faster and cheaper than buying reports
                            individually. Closing Strategy is included on every upload at
                            Pro and above.
                        </p>
                    </div>
                </div>

                <div className="mt-6 grid sm:grid-cols-3 gap-3" data-testid="paywall-investor-plans">
                    {INVESTOR_SNAPSHOTS.map((p) => (
                        <div
                            key={p.id}
                            className={`rounded-xl border bg-white p-4 ${p.highlight ? "border-bl-primary shadow-sm" : "border-bl"}`}
                            data-testid={`paywall-investor-${p.id}`}
                        >
                            {p.highlight && (
                                <div className="mb-2">
                                    <span className="bl-badge bl-badge-neutral text-[9px]">
                                        <Sparkles className="w-2.5 h-2.5" /> Best for active investors
                                    </span>
                                </div>
                            )}
                            <div className="font-display font-semibold text-bl text-base">
                                {p.name}
                            </div>
                            <div className="mt-1 flex items-baseline gap-1">
                                <span className="font-display font-semibold text-bl text-xl">
                                    {p.price}
                                </span>
                                <span className="text-bl-muted text-xs">/month</span>
                            </div>
                            <div className="mt-1 text-[10px] font-mono-bl text-bl-muted">
                                {p.quota}
                            </div>
                            <p className="mt-2 text-[11px] text-bl-muted leading-snug">
                                {p.tagline}
                            </p>
                            <ul className="mt-3 space-y-1.5">
                                {p.bullets.map((b) => (
                                    <li key={b} className="flex items-start gap-1.5 text-[11px] text-bl leading-snug">
                                        <Check className="w-3 h-3 text-bl-primary mt-0.5 shrink-0" />
                                        <span>{b}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                    <Link
                        to="/pricing#investor-plans"
                        onClick={() => track("paywall_investor_cta_clicked", { upload_id: uploadId })}
                        className="bl-btn-primary gap-2"
                        data-testid="paywall-investor-cta"
                    >
                        See investor plans
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                    <span className="text-xs text-bl-muted">
                        Cancel anytime. Annual plans save ~17%.
                    </span>
                </div>
            </div>
        </div>
    );
}
